import { OperationVariables, TypedDocumentNode } from '@apollo/client';

import { useMutation } from '@apollo/client/react';

import { useAuth } from '@domain/auth/AuthContext';
import { reportMykissClientError } from '@lib/errors/client';

// NOTE: The signature of this hook mimics that of useMutation, so if useMutation changes we should also
// update this. The `mutation` param is a non-union `TypedDocumentNode` (not `DocumentNode | …`) so that
// TData/TVariables infer from a typed document; a union would match the bare `DocumentNode` arm and
// drop inference to `any`/`OperationVariables`. Untyped `gql` documents still pass via the `any` default.
export function useRutilusMutation<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  mutation: TypedDocumentNode<TData, TVariables>,
  options: useMutation.Options<TData, TVariables> = {},
): useMutation.ResultTuple<TData, TVariables> {
  const { getToken, invalidateAccessToken } = useAuth();

  // We need to cast because TypeScript can't properly resolve Apollo's complex overloads
  // when called from within a generic wrapper. The runtime behavior matches our declared type.
  const result = useMutation(mutation, {
    ...options,
    context: { ...options.context, getAuthToken: getToken },
  } as any) as useMutation.ResultTuple<TData, TVariables>;

  if (result[1].error) {
    if (result[1].error.message.includes('an object of type Mutation is not accessible')) {
      invalidateAccessToken();
      reportMykissClientError(new Error('User had invalid access token'), undefined, 'warning');
    } else {
      reportMykissClientError(result[1].error);
    }
  }

  return result;
}
